import React, { useRef } from 'react';
import classNames from 'classnames';

import FormFieldError from './FormFieldError';
import FormFieldLabel from './FormFieldLabel';
import FormFieldTooltip, { TooltipSource } from './FormFieldTooltip';
import useFormField, { FieldEvent, FieldWidth } from './useFormField';

interface IProps {
  domain: string;
  name: string;
  label?: string;
  tooltip?: TooltipSource;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  width?: FieldWidth;
  onFocus?: FieldEvent<string>;
  onChange?: FieldEvent<string>;
  onBlur?: FieldEvent<string>;
}

function FormEmailField({
  name,
  label,
  tooltip,
  description,
  placeholder,
  disabled,
  width,
  domain,
  onFocus,
  onChange,
  onBlur,
}: IProps) {
  const inputRef = useRef(null);
  const {
    id,
    fieldProps,
    inputProps,
    setFieldValue,
    setFieldTouched,
    required,
    disabled: formDisabled,
  } = useFormField<string>(name, width, { onFocus, onChange, onBlur });

  const email = inputProps.value || '';
  const emailParts = email.split('@');
  const emailDomain = emailParts[1] || domain;
  const emailPrefix = emailParts[0] || '';

  const normalizeValue = (value: string): string => value.toLowerCase().split('@')[0];

  const onBlurHandler = () => setFieldTouched(name, true);
  const onChangeHandler = (e) => {
    if (!hasSameDomain) {
      return;
    }
    setFieldValue(name, `${normalizeValue(e.currentTarget.value)}@${domain}`);
  };

  const hasSameDomain = domain && emailDomain === domain;
  const isDisabled = formDisabled || disabled || !domain || !hasSameDomain;
  const inputClassName = classNames('novoInputWithSuffix', { 'novoInputWithSuffix--disabled': isDisabled });
  return (
    <div {...fieldProps}>
      {label && (
        <div className="novoFormField__name">
          <FormFieldLabel for={id} required={required}>
            {label}
          </FormFieldLabel>
          <FormFieldTooltip source={tooltip} />
        </div>
      )}
      {description && <p className="novoFormField__description">{description}</p>}
      <div className={inputClassName} tabIndex={0} onFocus={() => inputRef?.current.focus()}>
        <input
          className="novoFormField__input"
          id={id}
          tabIndex={-1}
          autoComplete="off"
          ref={inputRef}
          required={required}
          disabled={isDisabled}
          placeholder={placeholder}
          value={hasSameDomain && !isDisabled ? emailPrefix : email}
          onFocus={inputProps.onFocus}
          onChange={onChangeHandler}
          onBlur={onBlurHandler}
        />
        <span className="novoInputWithSuffix__suffix">{hasSameDomain && !isDisabled && `@${domain}`}</span>
        <span className="novoFormField__raw" aria-hidden="true">
          {email}
        </span>
      </div>
      <FormFieldError name={name} />
    </div>
  );
}

export default FormEmailField;
